import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef, SimpleChanges, OnChanges, OnDestroy, input, output, inject, signal, computed, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientService, TenantService, TimeconversionService } from '@fitness-force/api';
import { ClientStatus, DeletionRequestStatus } from '@fitness-force/enum';
import type { CLientReferral, ClientById, ClientCoupon, ClientInteraction, TENANTSINTERFACE, DeletionDetails, TENANT_MODEL } from '@fitness-force/models';
import { 
  TransactionTabComponent, 
  SubscriptionListComponent, 
  ClientMigrationTabComponent, 
  ClientFitnessTabComponent, 
  ClientBookingTabComponent, 
  ClientTrialTabComponent, 
  ClientTaskTabComponent,
  ClientInteractionTabComponent,
  ClientMilestoneTabComponent
} from '@fitness-force/shared';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SET_ACTIVE_TENANT, STAFF_STATE, STAGE_MASTER_STATE, TENANTS_INFO } from '@fitness-force/state';
import { ConfirmationService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { StyleClassModule } from 'primeng/styleclass';
import { ButtonModule } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin, from, of } from 'rxjs';
import { catchError, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { ClientProfileLeftSideComponent } from '../client-profile-left-side/client-profile-left-side.component';
import { ClientOverviewTabComponent } from '../client-overview-tab/client-overview-tab.component';
import { ClientProfileDetailsTabComponent } from '../client-profile-details-tab/client-profile-details-tab.component';
import { ClientCheckinTabComponent } from '../client-checkin-tab/client-checkin-tab.component';
import { ClientFormsTabComponent } from '../client-forms-tab/client-forms-tab.component';

import { FeatureFlagService, RouteStateService } from '@fitness-force/shared-utils';

const BASE_CLIENT_OVERVIEW_TABS = [
  { title: 'CLIENTSUMMARY', icon: '', key: 'clientsummary' },
  { title: 'OVERVIEW', icon: '', key: 'overview' },
  { title: 'ACTIVITY_FEED', icon: '', key: 'activity_feed' },
  { title: 'BOOKING', icon: '', key: 'booking' },
  { title: 'SUBSCRIPTIONS', icon: '', key: 'subscriptions' },
  { title: 'TRANSACTIONS', icon: '', key: 'transactions' },
  { title: 'TRIAL', icon: '', key: 'trial' },
  { title: 'TASK', icon: '', key: 'task' },
  { title: 'CHECK_IN', icon: '', key: 'check_in' },
  { title: 'FORMS', icon: '', key: 'forms' },
] as const;

@Component({
  selector: 'fitness-force-client-profile',
  templateUrl: './client-profile.component.html',

  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
  standalone: true,
  imports: [
    AvatarModule,
    TagModule,
    StyleClassModule,
    ConfirmDialogModule,
    BlockUIModule,
    MessageModule,
    TransactionTabComponent,
    SubscriptionListComponent,
    ClientTrialTabComponent,
    ClientBookingTabComponent,
    ClientMigrationTabComponent,
    ClientTaskTabComponent,
    ClientOverviewTabComponent,
    TooltipModule,
    SkeletonModule,
    TabsModule,
    ClientProfileLeftSideComponent,
    ClientProfileDetailsTabComponent,
    ClientCheckinTabComponent,
    ClientFormsTabComponent,
    ClientFitnessTabComponent,
    ClientMilestoneTabComponent,
    ClientInteractionTabComponent,
    ButtonModule,
    TranslateModule, DatePipe, NgClass]
})
export class ClientProfileComponent implements OnInit,OnChanges, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly activeRoute = inject(ActivatedRoute);
  private readonly tenantService = inject(TenantService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly clientService = inject(ClientService);
  readonly changeDetectorRef = inject(ChangeDetectorRef);
  readonly translate = inject(TranslateService);
  readonly datepipe = inject(DatePipe);
  readonly timeService = inject(TimeconversionService);
  readonly storeService = inject(RouteStateService);
  private readonly featureFlagService = inject(FeatureFlagService);

  readonly activeTab = input('overview');
  readonly activeSubTab = input('overview');
  readonly clientId = input(0);
  readonly interactionId = input<number>(0);
  readonly closeClientSidebar = output<boolean>();
  
  // Signals for component state
  readonly activeIndex = signal(1);
  readonly showClientSidebar = signal(true);
  readonly selectedMenu = signal<any>(null);
  readonly allTenants = signal<TENANT_MODEL[]>([]);
  readonly accessedTenants = signal<TENANT_MODEL[]>([]);
  readonly tenantId = signal(0);
  private readonly tenantSwitchPromptClientId = signal<number | null>(null);
  readonly allStaffs = signal<any[]>([]);
  readonly client = signal<ClientById | undefined>(undefined);
  readonly clientIds = signal<number[]>([]);
  readonly selectedTenant = signal<any>(null);
  readonly currentTenantClient = signal(true);
  readonly showAddCreditButton = signal(false);
  readonly showOrganization = signal(false);
  readonly requestParams = signal(new Map());
  readonly clientInteractions = signal<ClientInteraction[]>([]);
  readonly clickfromTab = signal(true);
  readonly showCollapsed = signal(true);
  readonly reloadClientData = signal(false);
  readonly clientReferrals = signal<CLientReferral[]>([]);
  readonly clientCoupons = signal<ClientCoupon[]>([]);
  readonly activityHeight = signal("53vh");
  readonly overViewHeight = signal("53vh");
  readonly clientUuid = signal('');
  readonly latestDeletionRequest = signal<DeletionDetails | null>(null);
  readonly enableSahha = signal(false);
  readonly showMilestoneManagement = signal(false);

  readonly clientOverviewTabs = computed(() => {
    const tabs: { title: string; icon: string; key: string }[] = [...BASE_CLIENT_OVERVIEW_TABS];

    if (this.showMilestoneManagement()) {
      tabs.push({ title: 'MILESTONES', icon: '', key: 'milestones' });
    }

    const client = this.client();
    if (client?.migration_id != null && client.migration_id !== '') {
      tabs.push({ title: 'MIGRATION', icon: 'fa fa-server', key: 'migration' });
    }

    if (this.enableSahha()) {
      tabs.push({ title: 'FITNESS', icon: '', key: 'fitness' });
    }

    return tabs;
  });

  // Legacy BehaviorSubjects for backward compatibility with child components
  readonly showSkeleton$ = new BehaviorSubject<boolean>(true);
  readonly allClients$ = new BehaviorSubject<ClientById>({} as ClientById);
  readonly isBlackListed = new BehaviorSubject<boolean>(false);
  readonly clientInteractions$ = new BehaviorSubject<ClientInteraction[]>([]);
  readonly clickfromTab$ = new BehaviorSubject<boolean>(true);
  readonly showCollapsed$ = new BehaviorSubject<boolean>(true);
  readonly clientReferrals$ = new BehaviorSubject<CLientReferral[]>([]);
  readonly clientCoupons$ = new BehaviorSubject<ClientCoupon[]>([]);

  // Loading state
  readonly isLoading = signal(false);

  // Observable for stage master (kept as Observable for store integration)
  stageMaster$: Observable<any> = new Subject();
  getAccessTenantList$: Observable<any> | any | null;
  private readonly destroy$ = new Subject<void>();

  constructor() {
    // Track clientId changes and update clientIds array
    effect(() => {
      const clientId = this.clientId();
      if (clientId && clientId > 0) {
        const currentIds = this.clientIds();
        if (!currentIds.includes(clientId)) {
          this.clientIds.update(ids => [...ids, clientId]);
        }
        // Load client data when clientId changes
        this.loadClientData();
      }
    });
  }

  private loadClientData() {
    const clientId = this.clientId();
    if (clientId) {
      if (this.tenantSwitchPromptClientId() !== clientId) {
        this.tenantSwitchPromptClientId.set(null);
      }
      this.isLoading.set(true);
      this.showSkeleton$.next(true);
      forkJoin({
        staffState: this.store.select(STAFF_STATE).pipe(
          take(1),
          catchError((error) => {
            console.error('Error fetching staff state:', error);
            return of({ staffList: [] });
          })
        ),
        clientDetails: this.clientService.getClientByIdNew(clientId).pipe(
          catchError((error) => {
            console.error('Error fetching client details:', error);
            return of(null);
          })
        )
      }).pipe(
        // Add timeout to prevent indefinite hanging
        timeout(30000) // 30 seconds timeout
      ).subscribe({
        next: (data) => {
          // Process the data immediately without creating reactive dependencies
          this.isLoading.set(false);
          this.showSkeleton$.next(false);
          
          if (data.clientDetails) {
            const client = data.clientDetails;
            this.client.set(client);
            this.allClients$.next(client);
            this.clientUuid.set(client?.uuid || '');
            
            // Get latest deletion request if client is deleted
            if (client.is_deleted) {
              this.latestDeletionRequest.set(this.getLatestDeletionRequest(client));
            }

            this.updateCurrentTenantClient(client);
            this.evaluateTenantSwitchDialog(client);

            this.storeService.setClient(client);
          } else {
            this.storeService.setClient(null);
          }

          if (data.staffState) {
            this.allStaffs.set((data.staffState as any).staffList || []);
          }
          
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          console.error('Error or timeout loading client data:', error);
          this.isLoading.set(false);
          this.showSkeleton$.next(false);
          this.changeDetectorRef.detectChanges();
        },
        complete: () => {
          // Ensure loading state is always cleared
          this.isLoading.set(false);
          this.showSkeleton$.next(false);
        }
      });
    }
  }

  ngOnInit(): void {
    // Wait for Statsig before reading flags — getFlag$ emits default false first,
    // so take(1) on combineLatest would skip optional tabs when initialization is slow.
    from(this.featureFlagService.waitForInitialization())
      .pipe(
        switchMap(() => combineLatest([
          this.featureFlagService.getFlag$('show_staff_2way_message_notifciation'),
          this.featureFlagService.getFlag$('enable_sahha'),
          this.featureFlagService.getFlag$('show_milestone_management')
        ])),
        takeUntil(this.destroy$)
      )
      .subscribe(([showStaff2Way, enableSahha, showMilestoneManagement]) => {
        if (!showStaff2Way) {
          this.activityHeight.set('84vh');
          this.overViewHeight.set('71vh');
        }
        this.enableSahha.set(enableSahha);
        this.showMilestoneManagement.set(showMilestoneManagement);
        this.changeDetectorRef.markForCheck();
      });
    
    this.componentInit();
    // Ensure the tab state is properly synchronized on initialization
    setTimeout(() => {
      const activeTab = this.activeTab();
      if (window.innerWidth <= 991) {
        this.activateTab('clientsummary');
      } else if (activeTab) {
        this.activateTab(activeTab);
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    // Note: With input signals and resource API, this is handled automatically
    // Resource API will reload when clientId() changes
      if (
        changes['clientId']?.currentValue && changes['clientId'].previousValue &&
        changes['clientId'].currentValue !== changes['clientId'].previousValue
      ) {
      // Resource API automatically reloads when request changes
      this.changeDetectorRef.detectChanges();
    }
  }

  componentInit() {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId.set(tenant.selected_tenant.id);
      this.allTenants.set(tenant.all_tenant);
      this.accessedTenants.set(tenant.accessed_tenant ?? []);
      this.selectedTenant.set(tenant.selected_tenant);
      const params = new Map(this.requestParams());
      params.set('tenant_id', { paramName: 'tenant_id', paramValue: this.tenantId() });
      this.requestParams.set(params);

      const client = this.client();
      if (client?.id) {
        this.updateCurrentTenantClient(client);
      }
    });
    this.stageMaster$ = this.store.select(STAGE_MASTER_STATE);
    // Resource API will automatically reload when clientId changes
  }

  // getClientData() is now handled by Resource API
  // The resource automatically reloads when clientId() changes

  readonly getAllParams = computed(() => {
    return [...this.requestParams().values()];
  });

  activateTab(tab: string) {
    try {
      const tabs = this.clientOverviewTabs();
      const tabIndex = tabs.findIndex(m => m.key.toLowerCase() === tab.toLowerCase());

      // Ensure activeIndex is valid
      if (tabIndex >= 0 && tabIndex < tabs.length) {
        this.activeIndex.set(tabIndex);
        this.selectedMenu.set(tabs[tabIndex]);
      } else {
        console.warn(`Tab key '${tab}' not found, defaulting to index 0`);
        this.activeIndex.set(0);
        this.selectedMenu.set(tabs[0]);
      }

      // Ensure change detection
      // this.changeDetectorRef.markForCheck();
      //this.changeDetectorRef.detectChanges();
    } catch (error) {
      console.error('Error activating tab:', error);
      // Default to first tab in case of error
      this.activeIndex.set(0);
      this.changeDetectorRef.detectChanges();
    }
  }

  readonly showSkeleton = computed(() => this.isLoading());
  
  readonly isLastTabActive = computed(() => {
    return this.activeIndex() === this.clientOverviewTabs().length - 1;
  });

  readonly clientTenantName = computed(() => {
    const client = this.client();
    if (!client?.id) {
      return '';
    }
    const clientTenantId = this.getClientRelevantTenantId(client);
    const tenant = this.accessedTenants().find((t) => t.id === clientTenantId)
      ?? this.allTenants().find((t) => t.id === clientTenantId);
    return tenant?.name_primary_language || tenant?.name_secondary_language || '';
  });

  closeSidebar(_event: any) {
    this.showClientSidebar.set(false);
    this.closeClientSidebar.emit(true);
  }

  switchToClientTenant(): void {
    const client = this.client();
    if (!client?.id) {
      return;
    }

    const clientTenantId = this.getClientRelevantTenantId(client);
    const selectedTenant = this.accessedTenants().find((tenant) => tenant.id === clientTenantId)
      ?? this.allTenants().find((tenant) => tenant.id === clientTenantId);

    if (!selectedTenant) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { tenant: clientTenantId.toString() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      preserveFragment: true,
    }).catch(() => {
      // Navigation can fail if already at same URL; ignore
    });
    this.store.dispatch(SET_ACTIVE_TENANT({ selected_tenant: selectedTenant }));
    this.tenantService.updateTenantPresaleStatus(selectedTenant);
    this.store.dispatch({ type: 'ROUTER_GO' });
  }

  private getClientRelevantTenantId(client: ClientById): number {
    if (client.client_status == ClientStatus.Client || client.client_status.toString() == ClientStatus[ClientStatus.Client]) {
      return client.home_club_tenant_id;
    }

    if (client.client_status == ClientStatus.Prospect || client.client_status.toString() == ClientStatus[ClientStatus.Prospect]
      || client.client_status == ClientStatus.Trial || client.client_status.toString() == ClientStatus[ClientStatus.Trial]) {
      return client.latest_createdin_tenant_id;
    }

    if (client.home_club_tenant_id && client.home_club_tenant_id > 0) {
      return client.home_club_tenant_id;
    }

    return client.latest_createdin_tenant_id;
  }

  private hasAccessToClientTenant(client: ClientById): boolean {
    const clientTenantId = this.getClientRelevantTenantId(client);
    if (!clientTenantId) {
      return false;
    }

    return this.accessedTenants().some((tenant) => tenant.id === clientTenantId);
  }

  private updateCurrentTenantClient(client: ClientById): void {
    const tenantId = this.tenantId();

    if (client.client_status == ClientStatus.Client || client.client_status.toString() == ClientStatus[ClientStatus.Client]) {
      this.currentTenantClient.set(tenantId == client.home_club_tenant_id);
    } else if (client.client_status == ClientStatus.Prospect || client.client_status.toString() == ClientStatus[ClientStatus.Prospect]
      || client.client_status == ClientStatus.Trial || client.client_status.toString() == ClientStatus[ClientStatus.Trial]) {
      this.currentTenantClient.set(tenantId == client.latest_createdin_tenant_id);
    } else {
      this.currentTenantClient.set(
        (tenantId == client.latest_createdin_tenant_id && tenantId == client.home_club_tenant_id) ||
        tenantId == client.home_club_tenant_id
      );
    }
  }

  private evaluateTenantSwitchDialog(client: ClientById): void {
    if (this.currentTenantClient() || !this.hasAccessToClientTenant(client)) {
      return;
    }

    if (this.tenantSwitchPromptClientId() === client.id) {
      return;
    }

    this.tenantSwitchPromptClientId.set(client.id);

    queueMicrotask(() => {
      this.confirmationService.confirm({
        key: 'clientTenantSwitch',
        header: this.translate.instant('CLIENT_PROFILE.TENANT_SWITCH.TITLE'),
        icon: '',
        message: '',
        accept: () => this.switchToClientTenant(),
      });
    });
  }

  changeTabClick(event: any) {
    if (event.changeTab) {
      this.clickfromTab.set(true);
      this.clickfromTab$.next(true);
      this.showCollapsed.set(false);
      this.showCollapsed$.next(false);
      this.activateTab(event.tabName);
    }
  }

  changeEvent(e: any) {
    if (e) {
      this.loadClientData();
    }
  }

  getClientDetails() {
    this.loadClientData();
  }

  // getClientEligibleCoupon() {
  //   this.showSkeleton$.next(true);
  //   this.clientService.getClientEligibleCoupons(this.tenantId, this.clientId).subscribe({
  //     next: (res) => {
  //       this.clientCoupons$.next(res.items ? res.items : []);
  //       this.showSkeleton$.next(false);
  //       this.changeDetectorRef.detectChanges();
  //     },
  //     error: (error: any) => {
  //       this.showSkeleton$.next(false);
  //       this.changeDetectorRef.detectChanges();
  //     }
  //   });
  // }

  updateData(event: boolean) {
    if (event) {
      this.reloadClientData.set(true);
      setTimeout(() => {
        this.getClientDetails();
      }, 1000);
      this.changeDetectorRef.detectChanges();
    }
  }

  reloadData() {
    //if(event.relaodData) this.getClientEligibleCoupon();
  }

  /**
   * Get the latest deletion request with status = Completed
   * Returns the most recent completed deletion request
   */
  getLatestDeletionRequest(client: ClientById): DeletionDetails | null {

    const deletionDetails = client.deletion ? JSON.parse(client.deletion) as DeletionDetails[] : [];
    if (!deletionDetails || !Array.isArray(deletionDetails) || deletionDetails.length === 0) {
      return null;
    }

    // Filter for completed deletion requests
    const completedRequests = deletionDetails.filter(
      (deletion: DeletionDetails) => deletion.request_status === DeletionRequestStatus.Completed || deletion.request_status.toString() === DeletionRequestStatus[DeletionRequestStatus.Completed]
    );


    if (completedRequests.length === 0) {
      return null;
    }

    // Sort by status_update_date descending and return the most recent
    return completedRequests.sort((a: DeletionDetails, b: DeletionDetails) =>
      new Date(b.status_update_date).getTime() - new Date(a.status_update_date).getTime()
    )[0];
  }

  ngOnDestroy(): void {
    // Cleanup destroy$ subject
    this.destroy$.next();
    this.destroy$.complete();
    
    // Complete BehaviorSubjects instead of unsubscribing
    if (this.showSkeleton$) {
      this.showSkeleton$.complete();
    }
    if (this.allClients$) {
      this.allClients$.complete();
    }
    if (this.isBlackListed) {
      this.isBlackListed.complete();
    }
    if (this.clientInteractions$) {
      this.clientInteractions$.complete();
    }
    if (this.clickfromTab$) {
      this.clickfromTab$.complete();
    }
    if (this.showCollapsed$) {
      this.showCollapsed$.complete();
    }
    // Signals don't need completion
    if (this.clientReferrals$) {
      this.clientReferrals$.complete();
    }
    if (this.clientCoupons$) {
      this.clientCoupons$.complete();
    }

    // Clear maps
    this.requestParams.set(new Map());
  }

  onTabChange(event: any) {
    try {
      const tabIndex = parseInt(event, 10);
      const tabs = this.clientOverviewTabs();
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex < tabs.length) {
        // Normal handling for tabs
        this.activeIndex.set(tabIndex);
        this.selectedMenu.set(tabs[tabIndex]);

      }
    } catch (error) {
      console.error('Error during tab change:', error);
    }
  }
}